import { cn } from '@amore/ui/cn'
import { Card } from '@amore/ui/components/card'
import { StatProgress } from '@amore/ui/components/stat'
import type { ReactNode } from 'react'

export type StatTile = {
  label: string
  value: ReactNode
  caption?: ReactNode
  tone?: 'default' | 'success' | 'warning' | 'danger'
  /** Makes the tile a filter link for the list below (or a link elsewhere). */
  onSelect?: () => void
  /** The tile's filter is the active one. */
  active?: boolean
}

export type StatGroup = {
  title: string
  /** Shown on the right of the title, e.g. "38 %". */
  value?: ReactNode
  progress?: { value: number; max: number; tone?: 'accent' | 'success' | 'warning'; label: string }
  tiles: StatTile[]
  /** One small line under the tiles. */
  footnote?: ReactNode
}

const valueTones = {
  default: 'text-foreground',
  success: 'text-success-foreground',
  warning: 'text-warning-foreground',
  danger: 'text-danger-foreground',
}

/**
 * The one KPI card (Přehled, Úkoly, Hosté, Rozpočet): an optional headline
 * metric, then one or two groups of two tiles. Same surface throughout: the
 * headline is a row of the card, not a differently coloured strip.
 */
export function StatSummary({
  headline,
  groups,
  aside,
}: {
  headline?: { label: string; value: ReactNode; caption?: ReactNode }
  groups: StatGroup[]
  /** Replaces the second group, e.g. a "Nastavte si rozpočet" prompt. */
  aside?: ReactNode
}) {
  return (
    <Card padding="none" className="overflow-hidden">
      {headline ? (
        <div className="space-y-1 border-b border-border px-5 py-4 sm:px-6">
          <p className="text-sm text-muted-foreground">{headline.label}</p>
          <p className="flex items-baseline gap-2">
            <strong className="text-3xl font-semibold tracking-tight tabular-nums">
              {headline.value}
            </strong>
            {headline.caption ? (
              <span className="text-base text-muted-foreground">{headline.caption}</span>
            ) : null}
          </p>
        </div>
      ) : null}
      <div
        className={cn(
          'grid gap-6 p-5 sm:p-6',
          (groups.length > 1 || aside) && 'lg:grid-cols-2 lg:gap-0 lg:divide-x lg:divide-border',
        )}
      >
        {groups.map((group, index) => (
          <div
            key={group.title}
            className={cn(
              'flex min-w-0 flex-col gap-4',
              index > 0 && 'border-t border-border pt-6 lg:border-t-0 lg:pt-0 lg:pl-6',
              index === 0 && (groups.length > 1 || aside) && 'lg:pr-6',
            )}
          >
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base font-semibold">{group.title}</h3>
              {group.value !== undefined ? (
                <span className="text-sm font-semibold tabular-nums">{group.value}</span>
              ) : null}
            </div>
            {group.progress ? (
              <StatProgress
                className="mt-0"
                value={group.progress.value}
                max={group.progress.max}
                tone={group.progress.tone}
                aria-label={group.progress.label}
              />
            ) : null}
            <div className="grid flex-1 auto-rows-fr grid-cols-2 gap-3">
              {group.tiles.map((tile) => (
                <Tile key={tile.label} tile={tile} />
              ))}
            </div>
            {group.footnote ? (
              <p className="text-xs text-muted-foreground">{group.footnote}</p>
            ) : null}
          </div>
        ))}
        {aside ? (
          <div className="min-w-0 border-t border-border pt-6 lg:border-t-0 lg:pt-0 lg:pl-6">
            {aside}
          </div>
        ) : null}
      </div>
    </Card>
  )
}

function Tile({ tile }: { tile: StatTile }) {
  const body = (
    <>
      <span className="text-xs font-semibold text-foreground">{tile.label}</span>
      <span
        className={cn(
          'text-2xl font-semibold tracking-tight tabular-nums',
          valueTones[tile.tone ?? 'default'],
        )}
      >
        {tile.value}
      </span>
      {tile.caption ? <span className="text-xs text-muted-foreground">{tile.caption}</span> : null}
    </>
  )
  const className = cn(
    'flex h-full flex-col items-start gap-1 rounded-2xl border border-border bg-card px-4 py-3 text-left',
    tile.active && 'border-action bg-accent/40',
  )
  return tile.onSelect ? (
    <button
      type="button"
      onClick={tile.onSelect}
      aria-pressed={tile.active}
      className={cn(
        className,
        'cursor-pointer transition-colors hover:border-action/50 hover:bg-accent/30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
      )}
    >
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  )
}
